import type { NettPaidBasis } from './resolve-statement-inputs'
import type { RefundUncertainty, UncertaintyOutcome } from './refund-uncertainty'
import type { SearchOpinion } from './search-opinion'
import type { AirlineDeductions, DeductionSource, DollarUncertaintyCode } from './types'

/**
 * How much to trust a computed RefundStatement — i.e. how likely the numbers are to match what
 * the supplier actually pays back. This is PLT's own calibration, independent of any confidence
 * value a GDS/calculator API might return for its own penalty figure — it scores the WHOLE
 * deduction resolution (source ladder position + every warning `resolveAirlineDeductions` already
 * emits), not just one input to it.
 *
 * Not a prediction of the refund amount's accuracy in isolation — `UNRESOLVED`/manual-entry cases
 * carry a score of 0 regardless of any other signal, because there is no automated figure to trust
 * in the first place.
 */
export type ConfidenceLevel = 'high' | 'medium' | 'low' | 'none'

/** One line of the arithmetic: `points` is signed — the source's base score first, then each deduction. */
export interface ConfidenceBreakdownItem {
  label: string
  points: number
}

export interface ConfidenceScore {
  /** 0-100. Higher = more reason to trust this statement without a manual check. */
  score: number
  level: ConfidenceLevel
  /** The weakest deduction source the score started from. */
  source: DeductionSource
  /** Plain-English reasons behind the score, in the order they were applied — safe to show ops/finance directly. */
  factors: string[]
  /** The same steps as `factors`, with the points each one added or took off, before clamping to 0-100. */
  breakdown: ConfidenceBreakdownItem[]
  /** The cost basis's own score (SPEC.md §10.3a), when it was scored: nothing may lift the score above it. */
  costBasisScore?: number
  /** Set when doubts measured in dollars matter (SPEC.md §10.8): nothing may lift the score above it. */
  uncertaintyCap?: number
}

/** The warning each dollar doubt is raised with; its points give way to the dollar check (§10.8). */
const UNCERTAINTY_WARNING: Record<DollarUncertaintyCode, string | undefined> = {
  'yqyr-unconfirmed': undefined,
  'conditional-tax-code': 'unverifiable-penalty-clause',
  'penalty-below-filed-rule': 'penalty-below-filed-rule',
  'unverifiable-penalty-clause': 'unverifiable-penalty-clause',
  'travel-credit-likely': 'travel-credit-likely',
}

const OUTCOME_DESCRIPTION: Record<UncertaintyOutcome, string> = {
  'customer-owes': 'the customer could owe money instead of getting a refund',
  'nothing-from-supplier': 'the supplier could send nothing back',
  'travel-credit': 'it could come back as a travel credit, not cash',
}

export type UncertaintyInput = Pick<
  RefundUncertainty,
  'issues' | 'settled' | 'withinTolerance' | 'outcomeChanges' | 'tolerance' | 'spread' | 'spreadCap'
>

/**
 * Base score per ladder rung (SPEC.md §3) — how directly the source reflects what the airline
 * will actually do, before any warnings are taken into account. VOID_WINDOW and OPERATOR_OVERRIDE
 * sit above SABRE_AUTOMATED_QUOTE because neither is an estimate at all: the first structurally
 * cannot have a penalty yet, the second is a human who already knows the answer.
 */
const SOURCE_BASE_SCORE: Record<DeductionSource, number> = {
  VOID_WINDOW: 100,
  OPERATOR_OVERRIDE: 95,
  AIRLINE_WAIVER: 80,
  SABRE_AUTOMATED_QUOTE: 90,
  SABRE_TICKET_NO_FEE: 85,
  SABRE_TICKET_PENALTY: 80,
  STRUCTURED_RULES: 55,
  PARSED_FARE_RULES: 45,
  AIRLINE_POLICY: 100,
  NON_REFUNDABLE_FARE: 60,
  UNRESOLVED: 0,
}

const SOURCE_DESCRIPTION: Record<DeductionSource, string> = {
  VOID_WINDOW: 'Still inside the void window — no cancellation penalty can apply yet.',
  OPERATOR_OVERRIDE: 'An ops-entered figure — authoritative, not an automated estimate.',
  AIRLINE_WAIVER:
    "A registered airline waiver sets each ticket's fee; the taxes that come back are still our estimate.",
  SABRE_AUTOMATED_QUOTE: "Sabre's own live refund quote for the ticket — the most direct source available.",
  SABRE_TICKET_NO_FEE:
    'Sabre confirms the ticket is refundable with no cancellation fee, on the exact amount paid for the ticket.',
  SABRE_TICKET_PENALTY: 'A GDS-quoted cancellation penalty, capped to the filed fare rule — an indirect estimate.',
  STRUCTURED_RULES: 'An AI-extracted reading of the filed fare rules — an estimate, not a live quote.',
  PARSED_FARE_RULES: 'A regex-parsed reading of the filed fare rules — cruder than the AI extraction.',
  AIRLINE_POLICY:
    "The airline's own published cancellation policy: Jetstar keeps the whole fare, so nothing comes back from it.",
  NON_REFUNDABLE_FARE:
    'Fare rules confirm the fare is non-refundable — penalty is the full base fare, only refundable taxes come back.',
  UNRESOLVED: 'No penalty could be resolved from any source.',
}

/**
 * Severity of each warning code `resolveAirlineDeductions` can emit (matched on the text before its
 * first `:`  — see resolve-deductions.ts for where each is raised). Unlisted/unrecognised warnings
 * still cost points via `UNKNOWN_WARNING_PENALTY`, so a new warning code added later degrades the
 * score instead of silently having no effect until someone remembers to list it here.
 */
const WARNING_IMPACT: Record<string, { penalty: number; note: string }> = {
  'no-penalty-data': {
    penalty: 100,
    note: 'No fare-rule or GDS penalty data exists at all for this ticket.',
  },
  'refundability-conflict': {
    penalty: 40,
    note: 'The GDS marks a ticket non-refundable but the fare rules do not confirm it and no penalty could be computed.',
  },
  'cat16-penalty-unconvertible': {
    penalty: 25,
    note: 'A filed cancellation fee could not be converted to the booking currency and was dropped.',
  },
  'override-conflict': {
    penalty: 20,
    note: 'Two operator-entered figures disagree with each other.',
  },
  'atco-partial-selection': {
    penalty: 20,
    note: 'A whole-booking penalty figure could not be pro-rated to this partial cancellation.',
  },
  'penalty-below-filed-rule': {
    // Informational: the quoted figure is kept as the best evidence until real cases show the filed fee wins.
    penalty: 0,
    note: "The GDS-quoted penalty sits far below the airline's own filed cancellation fee — worth a look, no points taken.",
  },
  'unverifiable-penalty-clause': {
    penalty: 15,
    note: 'The filed fare rule gates a further reduction on data we do not have — worth a manual check.',
  },
  'travel-credit-likely': {
    penalty: 15,
    note: 'A non-refundable AA, DL or UA fare usually comes back as a travel credit, not cash — confirm before processing.',
  },
  'exchanged-booking': {
    penalty: 15,
    note: "The tickets were reissued by a change, so the cost is rebuilt from Pipedrive's net fare and change fee — check it until changed bookings are proven.",
  },
  'change-fee-payment-unknown': {
    penalty: 15,
    note: 'The customer paid for a change, but the plan does not show whether that payment is inside it — check what they paid.',
  },
  'sabre-refunds-not-active': {
    // Shown, not weighted, until accuracy data says how much an estimated tax refund misses by.
    penalty: 0,
    note: "Sabre's automated refunds are not active for this ticket (49005), so the refunded taxes are our estimate — no points taken yet.",
  },
  'cat16-fx-suspect': {
    penalty: 10,
    note: 'A currency conversion for the filed penalty looked implausible and was discarded.',
  },
  'foreign-refund-totals': {
    penalty: 10,
    note: "The GDS quote was filed in a different currency than the booking and couldn't be used directly.",
  },
  'no-per-ticket-nett': {
    penalty: 10,
    note: 'No reliable per-ticket cost basis exists for this ticket.',
  },
  'booking-level-estimate': {
    penalty: 10,
    note: 'No ticket-level data — the estimate was derived from whole-booking fare rules instead.',
  },
  'implied-deduction-negative': {
    // Informational: the airline refunds more than the recorded cost, so the cost basis is the likely gap.
    penalty: 0,
    note: 'The GDS refund is higher than the amount recorded as paid for the ticket, so the deduction was set to zero — no points taken.',
  },
  'nett-paid-unknown': {
    penalty: 8,
    note: 'No cost basis from the GDS or ticket payments — the quote falls back to the sell price.',
  },
  'nett-paid-taxes-unknown': {
    penalty: 8,
    note: 'No tax breakdown for the selection — the amount paid to the supplier is fare-only.',
  },
  'untied-fees-partial-selection': {
    penalty: 8,
    note: 'Whole-booking fees could not be pro-rated to this partial cancellation.',
  },
  'nett-paid-pro-rated': {
    penalty: 10,
    note: "Pipedrive's booking-level net (commission included) was split by passenger count for this partial cancellation.",
  },
  'atpco-no-fee-unchecked': {
    // Informational: the PARSED_FARE_RULES base score (45 by default) already puts this at low.
    penalty: 0,
    note: 'ATPCO confirms a free cancellation and every coupon is open, but Sabre could not price the refund — confirm the fee before processing.',
  },
  'unresolved-ticket': {
    // Diagnostic only: an UNRESOLVED quote scores 0 before warnings apply.
    penalty: 0,
    note: 'No penalty from Sabre, the fare rules or ATPCO for this ticket.',
  },
  'jetstar-non-refundable': {
    penalty: 0,
    note: 'Jetstar refunds no cash on its bundles bar Business Max — the whole fare is withheld.',
  },
  'non-refundable-fare': {
    // Purely descriptive — the NON_REFUNDABLE_FARE base score already reflects this path's trust level.
    penalty: 0,
    note: 'Confirms the fare is non-refundable per the filed rules.',
  },
  'search-penalty-differs': {
    // Shown, not weighted: the search matched what airlines kept only about two times in three.
    penalty: 0,
    note: 'The fare search at booking offered a different penalty from the one read from the fare rules — check which is right.',
  },
}

/** Applied to any warning whose prefix isn't in the table above, so new warning codes degrade the score by default. */
const UNKNOWN_WARNING_PENALTY = 8

/**
 * The numbers ops can tune from the Ops App (SPEC.md §10.6): the starting score per source and the
 * points each known warning costs. Anything not in a stored version falls back to these defaults,
 * so a version saved before a source or warning existed still scores it.
 */
export interface ScoringSettings {
  baseScores: Partial<Record<DeductionSource, number>>
  warningPenalties: Partial<Record<string, number>>
  /**
   * The widest dollar range (booking currency) a quote can stay high with when no reading in it
   * changes the case (SPEC.md §10.8). Versions saved before it existed use the default.
   */
  uncertaintySpreadCap?: number
  /**
   * Points a quote read from the fare rules gains when the fare search at booking offered the same
   * penalty, never past 79. Off (0) until accuracy data says what agreement is worth.
   */
  searchAgreementPoints?: number
}

export const DEFAULT_UNCERTAINTY_SPREAD_CAP = 100
const MAX_UNCERTAINTY_SPREAD_CAP = 10_000

export const DEFAULT_SCORING_SETTINGS: ScoringSettings = {
  baseScores: { ...SOURCE_BASE_SCORE },
  warningPenalties: Object.fromEntries(Object.entries(WARNING_IMPACT).map(([code, impact]) => [code, impact.penalty])),
  uncertaintySpreadCap: DEFAULT_UNCERTAINTY_SPREAD_CAP,
  searchAgreementPoints: 0,
}

/** Plain-English label for each warning code, for the settings screen. */
export const WARNING_NOTES: Record<string, string> = Object.fromEntries(
  Object.entries(WARNING_IMPACT).map(([code, impact]) => [code, impact.note]),
)

/** Plain-English description for each source, for the settings screen. */
export const SOURCE_DESCRIPTIONS: Record<DeductionSource, string> = SOURCE_DESCRIPTION

const isScore = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 100

/**
 * Problems with a settings version, in words an owner can act on; empty when it is valid. Unresolved
 * stays at 0 because it means there is no figure at all, and only known sources and warnings are accepted
 * so a typo cannot silently create a new one.
 */
export function validateScoringSettings(settings: ScoringSettings): string[] {
  const problems: string[] = []
  for (const [source, score] of Object.entries(settings.baseScores)) {
    if (!(source in SOURCE_BASE_SCORE)) problems.push(`Unknown source ${source}`)
    else if (!isScore(score)) problems.push(`${source} must be a whole number from 0 to 100`)
    else if (source === 'UNRESOLVED' && score !== 0) problems.push('UNRESOLVED must stay at 0')
  }
  for (const [code, points] of Object.entries(settings.warningPenalties)) {
    if (!(code in WARNING_IMPACT)) problems.push(`Unknown warning ${code}`)
    else if (!isScore(points)) problems.push(`${code} must be a whole number from 0 to 100`)
  }
  const cap = settings.uncertaintySpreadCap
  if (cap !== undefined && !(Number.isInteger(cap) && cap >= 0 && cap <= MAX_UNCERTAINTY_SPREAD_CAP)) {
    problems.push(`The dollar range cap must be a whole number from 0 to ${MAX_UNCERTAINTY_SPREAD_CAP}`)
  }
  const agreement = settings.searchAgreementPoints
  if (agreement !== undefined && !isScore(agreement)) {
    problems.push('The points for agreeing with the fare search must be a whole number from 0 to 100')
  }
  return problems
}

/** The lowest score that reads `high`. */
const HIGH_SCORE = 80

/**
 * How the amount PLT paid the supplier was found (SPEC.md §2), scored apart from the airline
 * deductions (§10.3a): a refund is the cost less the deductions, so either side can be wrong.
 */
export interface CostBasisInput {
  basis: NettPaidBasis
  /** The cost-basis resolution's own warnings (`resolveNettPaidToSupplier`). */
  warnings: string[]
  /** No ticket was issued, so the supplier was never paid and the cost basis moves no refund. */
  noTicketsIssued?: boolean
}

const COST_BASIS_DESCRIPTION: Record<NettPaidBasis, string> = {
  PD_NET_FARE_PLUS_CHANGE: "Pipedrive's net fare plus the change fee, shared per passenger.",
  TICKET_PAYMENTS: 'The exact payment for each ticket.',
  GDS_FARE_PLUS_TAXES: "The GDS fare plus each ticket's own taxes.",
  PD_NET_FARE_COST: "Pipedrive's booking-level net fare.",
  GDS_FARE_ONLY: 'The GDS fare without its taxes.',
  TTV_ESTIMATE: 'The sell price, because nothing better was found.',
}

/** Bases that don't know what was paid: the taxes or the margin are missing, so the quote never reads `high`. */
const UNKNOWN_COST_BASES = new Set<NettPaidBasis>(['GDS_FARE_ONLY', 'TTV_ESTIMATE'])

function scoreCostBasis(input: CostBasisInput, settings: ScoringSettings): { score: number; notes: string[] } {
  let score = 100
  const notes: string[] = []
  for (const prefix of warningCodes(input.warnings)) {
    const impact = WARNING_IMPACT[prefix]
    score -= impact ? settings.warningPenalties[prefix] ?? impact.penalty : UNKNOWN_WARNING_PENALTY
    notes.push(impact ? impact.note : `Unrecognised warning "${prefix}" — treat with caution until reviewed.`)
  }
  if (UNKNOWN_COST_BASES.has(input.basis)) score = Math.min(score, HIGH_SCORE - 1)
  return { score: Math.max(0, Math.min(100, Math.round(score))), notes }
}

/** Deducted once when a multi-ticket booking mixes deduction sources across its tickets. */
const MIXED_SOURCE_PENALTY = 10

function warningPrefix(warning: string): string {
  const idx = warning.indexOf(':')
  return idx === -1 ? warning : warning.slice(0, idx)
}

export function levelFor(score: number): ConfidenceLevel {
  if (score <= 0) return 'none'
  if (score >= HIGH_SCORE) return 'high'
  if (score >= 50) return 'medium'
  return 'low'
}

/**
 * Score a resolved `AirlineDeductions` (from `resolveAirlineDeductions`) for how much to trust it
 * without a manual check. The deductions side re-derives everything it needs (source, warnings,
 * per-ticket mix) from that one object. Given the cost basis too, it scores that separately and
 * keeps the lower of the two (SPEC.md §10.3a).
 */
export function computeConfidenceScore(
  deductions: AirlineDeductions,
  settings: ScoringSettings = DEFAULT_SCORING_SETTINGS,
  costBasis?: CostBasisInput,
  uncertainty?: UncertaintyInput,
  search?: SearchOpinion,
): ConfidenceScore {
  if (deductions.requiresManualDeductions) {
    const notes = ['Deductions require manual entry — there is no automated figure to score.']
    if (search?.verdict === 'suggests') notes.push(search.note)
    return {
      score: 0,
      level: 'none',
      source: deductions.source,
      factors: notes,
      breakdown: notes.map(label => ({ label, points: 0 })),
    }
  }

  const factors: string[] = [SOURCE_DESCRIPTION[deductions.source]]
  let score = settings.baseScores[deductions.source] ?? SOURCE_BASE_SCORE[deductions.source]
  const breakdown: ConfidenceBreakdownItem[] = [
    { label: `${deductions.source}: ${SOURCE_DESCRIPTION[deductions.source]}`, points: score },
  ]

  const distinctTicketSources = new Set(deductions.perTicket.map(d => d.source))
  if (distinctTicketSources.size > 1) {
    score -= MIXED_SOURCE_PENALTY
    const note =
      `Tickets on this booking resolved from ${distinctTicketSources.size} different sources ` +
      `(${[...distinctTicketSources].sort().join(', ')}) — the aggregate reports the weakest one.`
    factors.push(note)
    breakdown.push({ label: note, points: -MIXED_SOURCE_PENALTY })
  }

  const seenPrefixes = new Map<string, number>()
  for (const warning of deductions.warnings) {
    const prefix = warningPrefix(warning)
    seenPrefixes.set(prefix, (seenPrefixes.get(prefix) ?? 0) + 1)
  }

  // A doubt the dollar check clears costs no points; one it doesn't keeps them, and caps the score below.
  const measured = new Set(
    uncertainty?.settled ? uncertainty.issues.map(i => UNCERTAINTY_WARNING[i.code]).filter(Boolean) : [],
  )
  for (const [prefix, count] of seenPrefixes) {
    const impact = WARNING_IMPACT[prefix]
    if (impact && measured.has(prefix)) {
      const note = count > 1 ? `${impact.note} (×${count})` : impact.note
      factors.push(note)
      breakdown.push({ label: `${prefix}: ${note} The dollar check below clears it.`, points: 0 })
    } else if (impact) {
      const penalty = settings.warningPenalties[prefix] ?? impact.penalty
      if (penalty > 0) {
        score -= penalty
        const note = count > 1 ? `${impact.note} (×${count})` : impact.note
        factors.push(note)
        breakdown.push({ label: `${prefix}: ${note}`, points: -penalty })
      } else if (impact.note) {
        const note = count > 1 ? `${impact.note} (×${count})` : impact.note
        factors.push(note)
        breakdown.push({ label: `${prefix}: ${note}`, points: 0 })
      }
    } else {
      score -= UNKNOWN_WARNING_PENALTY
      const note = `Unrecognised warning "${prefix}" — treat with caution until reviewed (×${count}).`
      factors.push(note)
      breakdown.push({ label: note, points: -UNKNOWN_WARNING_PENALTY })
    }
  }

  score = Math.max(0, Math.min(100, Math.round(score)))

  if (search?.verdict === 'agrees') {
    const points = settings.searchAgreementPoints ?? 0
    const gain = Math.max(0, Math.min(points, HIGH_SCORE - 1 - score))
    const effect =
      gain > 0
        ? 'Agreement adds points, up to 79 at most.'
        : points > 0
          ? 'Agreement cannot lift a quote past 79.'
          : 'Agreement earns no points yet.'
    factors.push(search.note)
    breakdown.push({ label: `${search.note} ${effect}`, points: gain })
    score += gain
  }

  let costBasisScore: number | undefined
  if (costBasis && !costBasis.noTicketsIssued) {
    const cost = scoreCostBasis(costBasis, settings)
    costBasisScore = cost.score
    factors.push(...cost.notes)
    const detail = [COST_BASIS_DESCRIPTION[costBasis.basis], ...cost.notes].join(' ')
    if (cost.score < score) {
      breakdown.push({
        label: `Cost basis ${costBasis.basis}: ${detail} It scores ${cost.score}, below the deductions, so the lower score is used.`,
        points: cost.score - score,
      })
      score = cost.score
    } else if (cost.notes.length > 0) {
      breakdown.push({ label: `Cost basis ${costBasis.basis}: ${detail} It scores ${cost.score}.`, points: 0 })
    }
  }

  let uncertaintyCap: number | undefined
  if (uncertainty && uncertainty.issues.length > 0) {
    const doubts = uncertainty.issues.map(i => i.label).join('; ')
    if (uncertainty.settled) {
      const why = uncertainty.withinTolerance
        ? `all of it sits inside the ±${uncertainty.tolerance.toFixed(2)} tolerance`
        : `no reading changes what happens to the case, and the range (${uncertainty.spread.toFixed(2)}) is within the ${uncertainty.spreadCap.toFixed(2)} cap`
      const note = `Dollar check: ${doubts} — ${why}, so no fact-check is needed.`
      factors.push(note)
      breakdown.push({ label: note, points: 0 })
    } else {
      uncertaintyCap = HIGH_SCORE - 1
      const reasons = uncertainty.outcomeChanges.map(o => OUTCOME_DESCRIPTION[o])
      if (uncertainty.spread > uncertainty.spreadCap) {
        reasons.push(
          `the range spans ${uncertainty.spread.toFixed(2)}, more than the ${uncertainty.spreadCap.toFixed(2)} a high quote may carry`,
        )
      }
      const why = reasons.join(', and ')
      const note = `Fact-check: ${doubts} — ${why}.`
      factors.push(note)
      breakdown.push({ label: note, points: Math.min(0, uncertaintyCap - score) })
      score = Math.min(score, uncertaintyCap)
    }
  }

  return {
    score,
    level: levelFor(score),
    source: deductions.source,
    factors,
    breakdown,
    costBasisScore,
    uncertaintyCap,
  }
}

/** The distinct warning codes in a list of warnings, sorted — the part before each warning's first colon. */
export function warningCodes(warnings: string[]): string[] {
  return [...new Set(warnings.map(warningPrefix))].sort()
}
